<template>
  <platform-modal
    v-model="showModal"
    icon="committee"
    :width="800"
    :save-disabled="!isValid"
    :title="$t('modal.committees.create.title')"
    @save="handleSave"
    @validate="$refs.form.validate()"
  >
    <committee-form
      ref="form"
      v-model="committee"
      show-type-option
      :company-id="selectedCompany ? selectedCompany.id : undefined"
      :saving="saving"
      :pending-members="pendingMemberNames"
      :all-available-members="allAvailableMemberNames"
      :hide-member-selector="hideExistingIndividuals"
      :import-completion-mode="importCompletionMode"
      :import-member-names="importMemberNames"
      :match-candidates="matchCandidates"
      @valid="(val) => isValid = val"
      @pendingMembersChanged="handlePendingMembersChanged"
    />
  </platform-modal>
</template>

<script>
import Vue from 'vue'
import { mapActions, mapState } from 'vuex'
import PlatformModal from '@/components/PlatformModal'
import CommitteeForm from '@/components/committees/CommitteeForm'
import { trimStringFields } from '@/helpers/validation'

export default Vue.extend({
  name: 'CreateCommitteeModal',
  components: {
    PlatformModal,
    CommitteeForm
  },
  props: {
    initialData: {
      type: Object,
      default: null
    },
    dataOnly: {
      type: Boolean,
      default: false
    },
    hideExistingIndividuals: {
      type: Boolean,
      default: false
    },
    importCompletionMode: {
      type: Boolean,
      default: false
    },
    importMemberNames: {
      type: Array,
      default: () => []
    },
    matchCandidates: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      timerId: null,
      showModal: true,
      committee: {
        name: null,
        tags: [],
        individuals: {
          items: []
        },
        companyId: null
      },
      pendingMemberNames: [],
      allAvailableMemberNames: [],
      nextToken: null,
      ready: false,
      saving: false,
      isValid: false
    }
  },
  created () {
    if (this.initialData) {
      this.committee = {
        ...this.committee,
        ...this.initialData
      }
      if (!this.importCompletionMode) {
        if (this.initialData.pendingMembers) {
          this.pendingMemberNames = [...this.initialData.pendingMembers]
        }
        if (this.initialData.allAvailableMembers) {
          this.allAvailableMemberNames = [...this.initialData.allAvailableMembers]
        }
      }
    }
  },
  computed: {
    ...mapState('Company', ['selectedCompany'])
  },
  watch: {
    showModal: function (val) {
      if (!val) {
        this.ready = false
        this.$emit('close')
      }
    }
  },
  methods: {
    ...mapActions('Committee', ['createCommittee']),
    handleSave () {
      if (this.dataOnly) {
        this.returnFormData()
      } else {
        this.save()
      }
    },
    handlePendingMembersChanged (members) {
      this.pendingMemberNames = members
    },
    returnFormData () {
      const data = trimStringFields({ ...this.committee })
      if (this.pendingMemberNames && this.pendingMemberNames.length > 0) {
        data.pendingMembers = this.pendingMemberNames
      }
      // Include selected individual IDs for existing members
      if (this.committee.individuals?.items?.length > 0) {
        data.existingIndividualIds = this.committee.individuals.items.map(i => i.individualId)
      }
      this.$emit('edited', data)
      this.showModal = false
    },
    async save () {
      if (this.saving) {
        return
      }

      this.saving = true
      this.createCommittee({
        name: this.committee.name,
        tags: this.committee.tags,
        individuals: this.committee.individuals.items
      })
        .then((response) => {
          this.dialog = false
          const committee = trimStringFields({
            ...this.committee,
            companyId: response.companyId,
            id: response.id,
            tags: response.tags,
            individuals: {
              items: this.committee.individuals.items.map((i) => ({ individualId: i.individualId }))
            }
          })
          this.$emit('created', committee)
          this.$toasted.success(this.$t('modal.committees.create.successMessage'))
        })
        .catch((err) => {
          console.error('Caught an error while trying to create a committee:', err)
          this.$toasted.error(this.$t('modal.committees.create.errorMessage'))
        })
        .finally(() => {
          this.saving = false
        })
    }
  }
})
</script>
